// A self-hosted Noto font for each script Inter does not cover, loaded only for
// the page's language (Vite emits one CSS + woff2 set per import, and the CJK
// ones are split by unicode-range, so a page fetches only the glyphs it shows).
// Self-hosted on purpose: fonts fetched from Google's servers send the visitor's
// address to Google, which German courts have held to be a GDPR breach.

type FontEntry = [family: string, load: () => Promise<unknown>];

const ARABIC: FontEntry = ["Noto Sans Arabic Variable", () => import("@fontsource-variable/noto-sans-arabic")];
const DEVANAGARI: FontEntry = ["Noto Sans Devanagari Variable", () => import("@fontsource-variable/noto-sans-devanagari")];
const BENGALI: FontEntry = ["Noto Sans Bengali Variable", () => import("@fontsource-variable/noto-sans-bengali")];

const FONTS: Record<string, FontEntry> = {
  ar: ARABIC,
  fa: ARABIC,
  ps: ARABIC,
  ur: ["Noto Nastaliq Urdu Variable", () => import("@fontsource-variable/noto-nastaliq-urdu")],
  he: ["Noto Sans Hebrew Variable", () => import("@fontsource-variable/noto-sans-hebrew")],
  hi: DEVANAGARI,
  mr: DEVANAGARI,
  ne: DEVANAGARI,
  bn: BENGALI,
  as: BENGALI,
  pa: ["Noto Sans Gurmukhi Variable", () => import("@fontsource-variable/noto-sans-gurmukhi")],
  gu: ["Noto Sans Gujarati Variable", () => import("@fontsource-variable/noto-sans-gujarati")],
  or: ["Noto Sans Oriya Variable", () => import("@fontsource-variable/noto-sans-oriya")],
  ta: ["Noto Sans Tamil Variable", () => import("@fontsource-variable/noto-sans-tamil")],
  te: ["Noto Sans Telugu Variable", () => import("@fontsource-variable/noto-sans-telugu")],
  kn: ["Noto Sans Kannada Variable", () => import("@fontsource-variable/noto-sans-kannada")],
  ml: ["Noto Sans Malayalam Variable", () => import("@fontsource-variable/noto-sans-malayalam")],
  si: ["Noto Sans Sinhala Variable", () => import("@fontsource-variable/noto-sans-sinhala")],
  th: ["Noto Sans Thai Variable", () => import("@fontsource-variable/noto-sans-thai")],
  lo: ["Noto Sans Lao Variable", () => import("@fontsource-variable/noto-sans-lao")],
  km: ["Noto Sans Khmer Variable", () => import("@fontsource-variable/noto-sans-khmer")],
  my: ["Noto Sans Myanmar Variable", () => import("@fontsource-variable/noto-sans-myanmar")],
  ka: ["Noto Sans Georgian Variable", () => import("@fontsource-variable/noto-sans-georgian")],
  hy: ["Noto Sans Armenian Variable", () => import("@fontsource-variable/noto-sans-armenian")],
  am: ["Noto Sans Ethiopic Variable", () => import("@fontsource-variable/noto-sans-ethiopic")],
  ja: ["Noto Sans JP Variable", () => import("@fontsource-variable/noto-sans-jp")],
  ko: ["Noto Sans KR Variable", () => import("@fontsource-variable/noto-sans-kr")],
  "zh-Hans": ["Noto Sans SC Variable", () => import("@fontsource-variable/noto-sans-sc")],
  "zh-Hant": ["Noto Sans TC Variable", () => import("@fontsource-variable/noto-sans-tc")],
};

// Scripts whose letters join or stack: letter-spacing and uppercase tracking
// break them, so candidate CSS turns both off (html[data-script="complex"]).
const COMPLEX = new Set(["ar", "fa", "ps", "ur", "hi", "mr", "ne", "bn", "as", "pa", "gu", "or", "ta", "te", "kn", "ml", "si", "th", "lo", "km", "my"]);

export function isComplexScript(code: string): boolean {
  return COMPLEX.has(code);
}

// Loads the font for `code` (a candidate catalog code) and puts its family in
// --font-script, which the candidate font stacks read. Latin, Cyrillic, Greek
// and Vietnamese need nothing: Inter has them.
export async function loadScriptFont(code: string): Promise<void> {
  const root = document.documentElement;
  const entry = FONTS[code];
  if (!entry) {
    root.style.removeProperty("--font-script");
    return;
  }
  await entry[1]();
  root.style.setProperty("--font-script", `"${entry[0]}"`);
}
